'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type * as React from 'react';
import { useState } from 'react';
import { AnimatedButton } from '@/components/ui/animated-button';
import { EmptyState } from '@/components/ui/empty-state';
import { Icon } from '@/components/ui/icon';
import { NewTwitterIconSolid } from '@/components/ui/icon/brands';
import { ArrowUpRight01Icon, Camera01Icon, Copy01Icon, Download01Icon } from '@/components/ui/icon/glyphs';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { SilkSheet } from '@/components/ui/silk-sheet';
import { StatusBanner } from '@/components/ui/status-banner';
import { TOAST_IDS } from '@/config/toast-ids';
import { onEvent } from '@/events';
import { copyToClipboard, openShareIntent } from '@/lib/share/share';
import { showToast } from '@/lib/ui/toast';
import assets from './clips.assets.generated.json';
import { REWARDS_CLIP_CONTENT, type RewardsClipContent } from './clips.content';

/** Replaced with the participant's own rewards referral link at render time. */
const LINK_TOKEN = '{{link}}';
/** Sign-up page shown until the participant's referral link has loaded. */
const FALLBACK_LINK = 'https://morfi.markets/';
interface ClipAsset {
  video: string;
  poster?: string;
  hash: string;
}
type Clip = RewardsClipContent & {
  video: string;
  poster?: string | undefined;
};

/**
 * Merge the hand-edited content ({@link REWARDS_CLIP_CONTENT}) with the
 * Blob URLs uploaded by `pnpm clips:sync` ({@link ./clips.assets.generated.json}).
 * A clip only renders once its video has been uploaded — so a fresh checkout
 * before the first sync degrades to the empty state rather than a broken player.
 */
const CLIPS: readonly Clip[] = REWARDS_CLIP_CONTENT.flatMap(content => {
  const asset = (assets as Record<string, ClipAsset>)[content.slug];
  return asset?.video ? [{
    ...content,
    video: asset.video,
    poster: asset.poster
  }] : [];
});

/**
 * "Clips ↗" affordance shown beside the info glyph in the clip-card header
 * (mirrors {@link import('./posts-button').PostsButton}). Opens a modal of
 * ready-to-post Morfi clips: a 1–N selector, the chosen clip's video, its
 * caption, and actions to post it to X, download the video, or copy the
 * caption. Each CTA carries the participant's own referral link so their post
 * drives tracked sign-ups.
 */
export function ClipsButton(t0) {
  const $ = _c(22);
  const {
    referralLink
  } = t0;
  const t = useTranslations("rewards");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      onEvent("rewards_content_kit_opened", {
        kind: "clip"
      });
      setOpen(true);
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== t) {
    t2 = t("clip.clipsLink");
    $[1] = t;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Icon icon={ArrowUpRight01Icon} className="size-3" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== t2) {
    t4 = <button type="button" data-testid="open-clips" onClick={t1} className="inline-flex shrink-0 cursor-pointer items-center gap-0.5 text-caption font-medium text-foreground-secondary transition-colors hover:text-foreground-primary">{t2}{t3}</button>;
    $[4] = t2;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t) {
    t5 = t("clip.clipsTitle");
    $[6] = t;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t) {
    t6 = CLIPS.length > 0 ? t("clip.clipsIntro") : undefined;
    $[8] = t;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== active || $[11] !== referralLink || $[12] !== t) {
    t7 = CLIPS.length > 0 ? <ClipsBody active={active} onActiveChange={setActive} referralLink={referralLink} /> : <EmptyState icon={Camera01Icon} title={t("clip.clipsEmptyTitle")} description={t("clip.clipsEmptyBody")} />;
    $[10] = active;
    $[11] = referralLink;
    $[12] = t;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  let t8;
  if ($[14] !== open || $[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <SilkSheet open={open} onOpenChange={setOpen} variant="detached" title={t5} description={t6}>{t7}</SilkSheet>;
    $[14] = open;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== t4 || $[20] !== t8) {
    t9 = <>{t4}{t8}</>;
    $[19] = t4;
    $[20] = t8;
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  return t9;
}
function ClipsBody(t0) {
  const $ = _c(69);
  const {
    active,
    onActiveChange,
    referralLink
  } = t0;
  const t = useTranslations("rewards");
  const index = Math.min(active, CLIPS.length - 1);
  const clip = CLIPS[index];
  if (!clip) {
    return null;
  }
  const link = referralLink ?? FALLBACK_LINK;
  let t1;
  if ($[0] !== clip.caption || $[1] !== link) {
    t1 = clip.caption.split(LINK_TOKEN).join(link);
    $[0] = clip.caption;
    $[1] = link;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const text = t1;
  let t2;
  if ($[3] !== text) {
    t2 = () => {
      onEvent("rewards_content_kit_used", {
        kind: "clip",
        action: "post_to_x"
      });
      openShareIntent("x", {
        url: "",
        text,
        surface: {
          kind: "support_mainnet"
        }
      });
    };
    $[3] = text;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const postToX = t2;
  let t3;
  if ($[5] !== clip.slug || $[6] !== clip.video || $[7] !== t) {
    t3 = () => {
      onEvent("rewards_content_kit_used", {
        kind: "clip",
        action: "download"
      });
      const a = document.createElement("a");
      a.href = `${clip.video}?download=1`;
      a.download = `morfi-${clip.slug}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      showToast({
        id: TOAST_IDS.REWARDS_CLIP_DOWNLOADED,
        message: t("clip.downloadStarted"),
        variant: "success"
      });
    };
    $[5] = clip.slug;
    $[6] = clip.video;
    $[7] = t;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const downloadClip = t3;
  let t4;
  if ($[9] !== t || $[10] !== text) {
    t4 = () => {
      onEvent("rewards_content_kit_used", {
        kind: "clip",
        action: "copy"
      });
      copyToClipboard(text).then(ok => {
        if (!ok) {
          return;
        }
        showToast({
          id: TOAST_IDS.REWARDS_CLIP_COPIED,
          message: t("clip.captionCopied"),
          variant: "success"
        });
      });
    };
    $[9] = t;
    $[10] = text;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const copyCaption = t4;
  let t5;
  if ($[12] !== t) {
    t5 = t("clip.downloadWarningTitle");
    $[12] = t;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t) {
    t6 = t("clip.downloadWarningBody");
    $[14] = t;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== t5 || $[17] !== t6) {
    t7 = <StatusBanner tone="warning" title={t5} description={t6} />;
    $[16] = t5;
    $[17] = t6;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== t) {
    t8 = t("clip.clipsTitle");
    $[19] = t;
    $[20] = t8;
  } else {
    t8 = $[20];
  }
  let t9;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = CLIPS.map(_temp);
    $[21] = t9;
  } else {
    t9 = $[21];
  }
  const t10 = String(index);
  let t11;
  if ($[22] !== onActiveChange) {
    t11 = key => onActiveChange(Number(key));
    $[22] = onActiveChange;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  let t12;
  if ($[24] !== t10 || $[25] !== t11 || $[26] !== t8) {
    t12 = <SegmentedControl variant="chart-filled" fullWidth={true} layoutId="rewards-clips-segment" ariaLabel={t8} items={t9} active={t10} onChange={t11} />;
    $[24] = t10;
    $[25] = t11;
    $[26] = t8;
    $[27] = t12;
  } else {
    t12 = $[27];
  }
  let t13;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = <track kind="captions" />;
    $[28] = t13;
  } else {
    t13 = $[28];
  }
  let t14;
  if ($[29] !== clip.id || $[30] !== clip.poster || $[31] !== clip.title || $[32] !== clip.video) {
    t14 = <video key={clip.id} src={clip.video} poster={clip.poster} aria-label={clip.title} controls={true} playsInline={true} preload="metadata" className="max-h-[80vh] w-auto max-w-full self-center rounded-panel-row border object-contain sm:max-h-[60vh]">{t13}</video>;
    $[29] = clip.id;
    $[30] = clip.poster;
    $[31] = clip.title;
    $[32] = clip.video;
    $[33] = t14;
  } else {
    t14 = $[33];
  }
  let t15;
  if ($[34] !== clip.title) {
    t15 = <p className="text-body font-medium text-foreground-primary">{clip.title}</p>;
    $[34] = clip.title;
    $[35] = t15;
  } else {
    t15 = $[35];
  }
  let t16;
  if ($[36] !== text) {
    t16 = <p className="whitespace-pre-line text-body text-foreground-secondary">{text}</p>;
    $[36] = text;
    $[37] = t16;
  } else {
    t16 = $[37];
  }
  let t17;
  if ($[38] !== t15 || $[39] !== t16) {
    t17 = <div className="flex flex-col gap-1.5">{t15}{t16}</div>;
    $[38] = t15;
    $[39] = t16;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  let t18;
  if ($[41] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = <Icon icon={Download01Icon} className="size-4" />;
    $[41] = t18;
  } else {
    t18 = $[41];
  }
  let t19;
  if ($[42] !== t) {
    t19 = t("clip.download");
    $[42] = t;
    $[43] = t19;
  } else {
    t19 = $[43];
  }
  let t20;
  if ($[44] !== downloadClip || $[45] !== t19) {
    t20 = <AnimatedButton variant="primary" className="w-full" data-testid="download-clip" onClick={downloadClip}>{t18}{t19}</AnimatedButton>;
    $[44] = downloadClip;
    $[45] = t19;
    $[46] = t20;
  } else {
    t20 = $[46];
  }
  let t21;
  if ($[47] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <Icon icon={NewTwitterIconSolid} className="size-4" />;
    $[47] = t21;
  } else {
    t21 = $[47];
  }
  let t22;
  if ($[48] !== t) {
    t22 = t("clip.postToX");
    $[48] = t;
    $[49] = t22;
  } else {
    t22 = $[49];
  }
  let t23;
  if ($[50] !== postToX || $[51] !== t22) {
    t23 = <AnimatedButton variant="secondary" className="w-full" data-testid="post-clip-x" onClick={postToX}>{t21}{t22}</AnimatedButton>;
    $[50] = postToX;
    $[51] = t22;
    $[52] = t23;
  } else {
    t23 = $[52];
  }
  let t24;
  if ($[53] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = <Icon icon={Copy01Icon} className="size-4" />;
    $[53] = t24;
  } else {
    t24 = $[53];
  }
  let t25;
  if ($[54] !== t) {
    t25 = t("clip.copyCaption");
    $[54] = t;
    $[55] = t25;
  } else {
    t25 = $[55];
  }
  let t26;
  if ($[56] !== copyCaption || $[57] !== t25) {
    t26 = <AnimatedButton variant="secondary" className="w-full" data-testid="copy-clip-caption" onClick={copyCaption}>{t24}{t25}</AnimatedButton>;
    $[56] = copyCaption;
    $[57] = t25;
    $[58] = t26;
  } else {
    t26 = $[58];
  }
  let t27;
  if ($[59] !== t20 || $[60] !== t23 || $[61] !== t26) {
    t27 = <div className="flex flex-col gap-2">{t20}{t23}{t26}</div>;
    $[59] = t20;
    $[60] = t23;
    $[61] = t26;
    $[62] = t27;
  } else {
    t27 = $[62];
  }
  let t28;
  if ($[63] !== t12 || $[64] !== t14 || $[65] !== t17 || $[66] !== t27 || $[67] !== t7) {
    t28 = <div className="flex flex-col gap-3">{t7}{t12}{t14}{t17}{t27}</div>;
    $[63] = t12;
    $[64] = t14;
    $[65] = t17;
    $[66] = t27;
    $[67] = t7;
    $[68] = t28;
  } else {
    t28 = $[68];
  }
  return t28;
}
function _temp(c, i) {
  return {
    key: String(i),
    label: String(i + 1),
    ariaLabel: c.title
  };
}